import PropTypes from 'prop-types';

import {
  Box,
  Text,
  Link,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  DrawerCloseButton,
  Stack,
  useDisclosure,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component as ContentfulComponent} from '@amblique/contentful/app/components/contentful/component';
import {Image} from '@amblique/king-living/app/contentful/models/image';
import {PlusIcon} from '@amblique/king-living/app/components/icons';

export const TeaserTile = ({component, isComplete = true, isFetching = false, ...props}) => {
  const {teaserText, image, descriptionCollection, openInNewTab, link} = component;
  const {isOpen, onOpen, onClose} = useDisclosure();

  const hasDescription = descriptionCollection?.items?.length > 0;

  return (
    <Box {...props}>
      {/* Image Container */}
      <Box overflow="hidden" position="relative">
        {image && (
          <Image component={image} isComplete={isComplete} isFetching={isFetching} />
        )}

        {/* Plus Icon - Bottom Left, triggers drawer */}
        {hasDescription && (
          <Box
            _hover={{bg: 'blackAlpha.800', cursor: 'pointer'}}
            alignItems="center"
            as="button"
            bg="blackAlpha.600"
            borderRadius="full"
            bottom={4}
            display="flex"
            h={8}
            justifyContent="center"
            left={4}
            position="absolute"
            transition="all 0.2s"
            w={8}
            onClick={onOpen}
          >
            <PlusIcon boxSize={4} color="white" />
          </Box>
        )}
      </Box>

      {/* Teaser Text */}
      {teaserText && (
        <Text
          color="brandColors.greyscale90"
          fontSize={{base: '14px', lg: '20px'}}
          fontWeight="medium"
          lineHeight="120%"
          mt={1}
        >
          {teaserText}
        </Text>
      )}

      {/* Link */}
      {link && (
        <Link
          _hover={{textDecoration: 'underline'}}
          color="black"
          display="block"
          fontSize="14px"
          href={link}
          isExternal={openInNewTab}
          mt={2}
        >
          View more
        </Link>
      )}

      {/* Description Drawer */}
      {hasDescription && (
        <Drawer blockScrollOnMount={true} isOpen={isOpen} placement="right" size="md" onClose={onClose}>
          <DrawerOverlay backdropFilter="blur(8px)" bg="blackAlpha.300" />
          <DrawerContent bg="brandColors.primaryBG">
            <DrawerCloseButton bg="none" color="white" fontSize="24px" />
            <DrawerBody padding="0">
              <Stack spacing={4}>
                {descriptionCollection.items.map((item, index) => (
                  index === 0 ? (
                    <Box key={item.sys.id}>
                      <ContentfulComponent
                        component={item}
                        isComplete={isComplete}
                        isFetching={isFetching}
                      />
                    </Box>
                  ) : (
                    <Box key={item.sys.id} padding="0 40px">
                      <ContentfulComponent
                        component={item}
                        isComplete={isComplete}
                        isFetching={isFetching}
                      />
                    </Box>
                  )
                ))}
              </Stack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
    </Box>
  );
};

TeaserTile.displayName = 'TeaserTile';
TeaserTile.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
